import React, { useState, useEffect } from "react";
import FastAPIClient from "../../../client";
import config from "../../../config";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import { FiX } from "react-icons/fi";
import { frFR } from "@mui/x-data-grid/locales";

const client = new FastAPIClient(config);

interface User {
    user_id: string;
    user_role_id: string;
    warehouse_id: string;
}

interface ClientCoverageRow {
    client_id: string;
    client_name: string;
    city: string;
    sales_rep_name: string;
    warehouse_name: string;
    visit_frequency: string;
    [key: string]: string | number; // for dynamic columns like "2024_4", which are typically numbers
}

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;
const currentYearMonth = `${currentYear}_${currentMonth}`;

const CoverageTable: React.FC = () => {
    const [data, setData] = useState<ClientCoverageRow[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([
        currentYearMonth,
    ]);
    const [user, setUser] = useState<User | null>(null);
    const [gridColumns, setGridColumns] = useState<GridColDef[]>([]);
    const [loading, setLoading] = useState(true);

    const getUserName = async () => {
        const userData: User = await client.fetchUser();
        setUser(userData);
    };

    useEffect(() => {
        getUserName();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!user?.user_id) return;

            setLoading(true); // Start loading
            const response = await client.getClientCoverage();

            setData(response.results);

            if (response.results.length > 0) {
                const keys = Object.keys(response.results[0]).filter(
                    (key) =>
                        ![
                            "client_id",
                            "client_name",
                            "city",
                            "sales_rep_name",
                            "warehouse_name", // Include warehouse_name in filtered keys for dynamic columns if needed, or handle separately
                            "visit_frequency",
                        ].includes(key)
                );
                setColumns(keys);
            }
            setLoading(false); // End loading
        };

        fetchData();
    }, [user, selectedColumns]);

    useEffect(() => {
        const baseColumns: GridColDef[] = [
            {
                field: "warehouse_name",
                headerName: "Dépôt",
                width: 150,
            },
            {
                field: "sales_rep_name",
                headerName: "Commercial",
                width: 150,
            },
            {
                field: "city",
                headerName: "Ville",
                width: 150,
                // Add renderCell for formatting if needed, similar to RevenueTable
            },
            {
                field: "client_name",
                headerName: "Nom client",
                width: 200,
                // Add renderCell for formatting if needed, similar to RevenueTable
            },
            {
                field: "client_id",
                headerName: "ID client",
                width: 120,
            },
            {
                field: "visit_frequency",
                headerName: "Fréquence de visite mensuelle cible",
                width: 200,
                renderCell: (params) => {
                    // Display formatted visit frequency
                    switch (params.value) {
                        case "1":
                            return "1x par mois";
                        case "2":
                            return "2x par mois";
                        case "3":
                            return "3x par mois";
                        case "4":
                            return "4x par mois";
                        default:
                            return "-"; // Fallback
                    }
                },
            },
            // Dynamically add columns for selected months
            ...selectedColumns.map((col) => ({
                field: col,
                headerName: col,
                width: 200,
                type: "number" as const, // Assuming visit counts are numbers
                sortable: false,
                renderCell: (params) => {
                    const value = Number(params.value);
                    const threshold = getThresholdValue(
                        params.row.visit_frequency
                    );

                    let backgroundColor = "";
                    if (!isNaN(value)) {
                        if (value >= threshold) {
                            backgroundColor = "#d0f0c0"; // Light green for met or exceeded
                        } else if (value > 0 && value < threshold) {
                            backgroundColor = "#ffe5b4"; // Lighter orange for some visits but below threshold
                        } else if (value === 0) {
                            backgroundColor = "#ffc0cb"; // Light pink for zero visits
                        }
                    }

                    return (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor,
                            }}
                        >
                            {value || "-"}
                        </Box>
                    );
                },
                renderHeader: () => {
                    const [year, month] = col.split("_");
                    const monthsInFrench = [
                        "Janvier",
                        "Février",
                        "Mars",
                        "Avril",
                        "Mai",
                        "Juin",
                        "Juillet",
                        "Août",
                        "Septembre",
                        "Octobre",
                        "Novembre",
                        "Décembre",
                    ];
                    const monthNameInFrench =
                        monthsInFrench[parseInt(month, 10) - 1];
                    return (
                        <Box>
                            <div>{`# Visites (${monthNameInFrench} ${year})`}</div>
                        </Box>
                    );
                },
            })),
        ];

        setGridColumns(baseColumns);
    }, [selectedColumns, data]);

    const handleAddColumn = (column: string) => {
        setSelectedColumns((prev) => [...prev, column]);
    };

    const handleRemoveColumn = (column: string) => {
        setSelectedColumns((prev) => prev.filter((col) => col !== column));
    };

    const getThresholdValue = (visitFrequency: string): number => {
        switch (visitFrequency) {
            case "1":
                return 1;
            case "2":
                return 2;
            case "3":
                return 3;
            case "4":
                return 4;
            default:
                return 0;
        }
    };

    return (
        <div>
            <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
            >
                {columns
                    .filter((col) => {
                        // Filter out columns before February 2025
                        const [year, month] = col.split("_").map(Number);
                        // Represent Feb 2025 as a number for comparison (e.g., 202502)
                        const cutoffValue = 202502;
                        const columnValue = year * 100 + month;
                        return columnValue >= cutoffValue;
                    })
                    .map((col) => {
                        const [year, month] = col.split("_");
                        const monthsInFrench = [
                            "Janvier",
                            "Février",
                            "Mars",
                            "Avril",
                            "Mai",
                            "Juin",
                            "Juillet",
                            "Août",
                            "Septembre",
                            "Octobre",
                            "Novembre",
                            "Décembre",
                        ];
                        const monthNameInFrench =
                            monthsInFrench[parseInt(month, 10) - 1];
                        const label = `${monthNameInFrench} ${year}`;
                        const isSelected = selectedColumns.includes(col);

                        return (
                            <Chip
                                key={col}
                                label={label}
                                onClick={() => {
                                    if (!isSelected) {
                                        handleAddColumn(col);
                                    }
                                }}
                                onDelete={
                                    isSelected
                                        ? () => handleRemoveColumn(col)
                                        : undefined
                                }
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                deleteIcon={
                                    isSelected ? <FiX size={16} /> : undefined
                                }
                            />
                        );
                    })}
            </Stack>

            <div className="overflow-x-auto">
                {loading ? (
                    <Box
                        sx={{
                            height: 600,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: 600, width: "100%" }}>
                        <DataGridPremium
                            localeText={
                                frFR.components.MuiDataGrid.defaultProps
                                    .localeText
                            }
                            rows={data}
                            columns={gridColumns}
                            getRowId={(row) => row.client_id}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 25 },
                                },
                                sorting: {
                                    sortModel: [
                                        {
                                            field: "warehouse_name",
                                            sort: "asc",
                                        },
                                        {
                                            field: "sales_rep_name",
                                            sort: "asc",
                                        },
                                        { field: "city", sort: "asc" },
                                    ],
                                },
                            }}
                            pageSizeOptions={[25, 50, 100]}
                            disableRowSelectionOnClick
                            getRowHeight={() => "auto"}
                            showToolbar
                            sx={{
                                "& .MuiDataGrid-cell": {
                                    borderColor: "rgba(224, 224, 224, 1)",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "white",
                                },
                            }}
                        />
                    </Box>
                )}
            </div>
            {/* Dummy usage to satisfy linter - should not be visible */}
            <div style={{ display: "none" }}>
                <Chip label="" style={{ display: "none" }} />
                <Stack style={{ display: "none" }}></Stack>
            </div>
        </div>
    );
};

export default CoverageTable;
